import { useCallback, useEffect, useState } from 'react';
import api from '../api/client';

export interface Notification {
  id: number;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'error' | 'success';
  is_read: boolean;
  created_at: string;
  related_employee?: number;
}

export function useNotifications() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Sortie du useEffect pour que `refetch` puisse la rappeler : elle y était
  // enfermée, si bien que `refetch` se contentait d'un window.location.reload()
  // — un rechargement complet de la page pour rafraîchir une pastille.
  const charger = useCallback(async () => {
    try {
      const { data } = await api.get('alerts/');

      // Réponse paginée ou non
      const alerts = Array.isArray(data) ? data : (data.results || []);

      const mapped: Notification[] = alerts.map((alert: any) => {
        let type: 'info' | 'warning' | 'error' | 'success' = 'info';
        if (alert.priority === 'critical') type = 'error';
        else if (alert.priority === 'high') type = 'warning';
        else if (alert.priority === 'medium') type = 'warning';

        return {
          id: alert.id,
          title: alert.title,
          message: alert.message,
          type,
          is_read: alert.status !== 'active',
          created_at: alert.created_at,
          related_employee: alert.employee || undefined,
        };
      });

      setNotifications(mapped);
      setError(null);
    } catch (err: any) {
      console.error('Chargement des notifications impossible', err);
      setError(err?.response?.data?.detail || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    charger();
    const intervalId = setInterval(charger, 30000);
    return () => clearInterval(intervalId);
  }, [charger]);

  const markAsRead = async (id: number) => {
    try {
      await api.post(`alerts/${id}/perform_action/`, { action: 'mark_read' });
      setNotifications(prev =>
        prev.map(n => n.id === id ? { ...n, is_read: true } : n)
      );
    } catch (err) {
      console.error('Erreur marquage notification:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const unreadIds = notifications.filter(n => !n.is_read).map(n => n.id);
      await Promise.all(
        unreadIds.map(id => api.post(`alerts/${id}/perform_action/`, { action: 'mark_read' }))
      );
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    } catch (err) {
      console.error('Erreur marquage toutes notifications:', err);
    }
  };

  const deleteOne = async (id: number) => {
    try {
      await api.delete(`alerts/${id}/`);
      setNotifications(prev => prev.filter(n => n.id !== id));
    } catch (err) {
      console.error('Erreur suppression notification:', err);
      throw err;
    }
  };

  const deleteMany = async (ids: number[]) => {
    try {
      await Promise.all(ids.map(id => api.delete(`alerts/${id}/`)));
      setNotifications(prev => prev.filter(n => !ids.includes(n.id)));
    } catch (err) {
      console.error('Erreur suppression notifications:', err);
      throw err;
    }
  };

  const refetch = charger;

  const unreadCount = notifications.filter(n => !n.is_read).length;


  return {
    notifications,
    unreadCount,
    loading,
    error,
    markAsRead,
    markAllAsRead,
    deleteOne,
    deleteMany,
    refetch,
  };
}
